<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useIdeStore } from '@/stores/ide.store'
import { useTabStore, type ActiveTab } from '@/stores/tabs.store'
import tabsService from '@/services/ide/tabs.service'
import { IDEVIEWTABS, TABS_WIDTH_TYPE } from '@/utils/tabs'
import OutputComp from '@/components/ide/ide/OutputComp.vue'
import GenaratedFileComp from '@/components/ide/ide/GenaratedFileComp.vue'
import InputFileComp from '@/components/ide/ide/InputFileComp.vue'
import LibraryComp from '@/components/ide/ide/LibraryComp.vue'
import Jdroid from '@/components/ide/ide/ai/jdroid/JDroidComp.vue'
import TabCompTitleBar from '@/components/ide/ide/TabCompTitleBar.vue'
import InputComp from '@/components/ide/ide/InputComp.vue'
import TabsComponent from '@/components/ide/ide/OutputAndGeneratedFilesTabComp.vue'
import OutputHeader from '@/components/ide/ide/OutputHeader.vue'
import GeneratedFileHeader from '@/components/ide/ide/GeneratedFileHeader.vue'
import BrowserComp from '@/components/ide/ide/BrowserComp.vue'
import DbTerminal from '@/components/ide/ide/DatabaseTerminal.vue'
import ApiTabComp from '@/components/ide/ide/tabs/ApiTabComp/ApiTabComp.vue'
import TerminalComp from '@/components/ide/ide/TerminalComp.vue'
import ConsoleComp from '@/components/ide/ide/ConsoleComp.vue'
import { TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'
import inputOutputIcon from '@/assets/images/ide/device-desktop-code.svg'
import arrowMaximize from '@/assets/images/ide/arrows-diagonal.svg'

const ideStore = useIdeStore()
const tabStore = useTabStore()

const isFullScreen = computed(() => {
  return ideStore.isFullScreen
})

const isTabMaximized = computed(() => {
  return tabStore.isTabMaximized
})

const isShowLibs = computed(() => {
  return ideStore.ideMeta?.isPlus && ideStore.ideMeta?.library
})

const showIORef = computed(() => {
  return tabsService.checkVisibileMaximize(TAB_ICONS_NAMES.IO)
})
const showExtLibRef = computed(() => {
  return tabsService.checkVisibileMaximize(TAB_ICONS_NAMES.EXTERNAL_LIBRARIES)
})
const showUploadResRef = computed(() => {
  return tabsService.checkVisibileMaximize(TAB_ICONS_NAMES.UPLOAD_FILES)
})
const showJroidRef = computed(() => {
  return tabsService.checkVisibileMaximize(TAB_ICONS_NAMES.JDROID)
})

const showBrowserRef = computed(() => {
  return tabsService.checkVisibileMaximize(TAB_ICONS_NAMES.BROWSER)
})

const showDbTerminalRef = computed(() => {
  return tabsService.checkVisibileMaximize(TAB_ICONS_NAMES.DB_TERMINAL)
})

const showApiTabRef = computed(() => {
  return tabsService.checkVisibileMaximize(TAB_ICONS_NAMES.API_TAB)
})

const showTerminalRef = computed(() => {
  return tabsService.checkVisibileMaximize(TAB_ICONS_NAMES.TERMINAL)
})

const showConsoleRef = computed(() => {
  return tabsService.checkVisibileMaximize(TAB_ICONS_NAMES.CONSOLE)
})

const currentMobileTab = computed(() => {
  return ideStore.currentMobileTab
})

/**
 * Extracts the name of the maximized (visible) tab.
 *
 * This function searches through the active tabs to find a tab that is currently visible.
 * If a visible tab is found, it returns the tab's name; otherwise, it returns `undefined`.
 * @returns The name of the visible tab if one exists; otherwise, `undefined`.
 */
const extractMaximizedTabName = () => {
  const tabs = tabStore.activeTabPosition.activeTab
  const visibleTab = tabs.find(
    (item: ActiveTab) => item.position == TAB_BUTTON_POSITION.MAXIMIZE && item.visible
  )
  if (visibleTab) {
    return [visibleTab.previousPosition, visibleTab.tabName]
  }
  return []
}
/**
 * Toggles the maximize state of a tab.
 */
const toggleMaximize = () => {
  const tabName = extractMaximizedTabName()
  if (tabName?.length > 1) {
    tabStore.setActiveTabPosition(tabName[0] as TAB_BUTTON_POSITION, tabName[1] as TAB_ICONS_NAMES)
  }
}

onMounted(() => {
  const tabName = extractMaximizedTabName()
  if (tabName?.length > 1) {
    if ((tabName[0] as TAB_BUTTON_POSITION) == TAB_BUTTON_POSITION.RIGHT) {
      tabStore.setTabWidthCurrentSize(TABS_WIDTH_TYPE.LARGE, tabName[1] as TAB_ICONS_NAMES)
    } else {
      tabStore.setTabWidthCurrentSizeBottom(TABS_WIDTH_TYPE.LARGE, tabName[1] as TAB_ICONS_NAMES)
    }
  }
})
</script>

<template>
  <div class="section-quinary flex h-full w-full">
    <div
      class="section-quinary border-primary z-40 h-full w-full overflow-auto border-l sm:static sm:min-w-[425px] sm:overflow-visible lg:w-[100%] lg:min-w-[460px]"
    >
      <div class="flex h-full w-full flex-col sm:sticky sm:top-20">
        <div class="h-full w-full">
          <div class="absolute right-4 top-1 z-40">
            <button
              class="border-primary pb-2 pl-2 pt-2 text-xs font-medium leading-normal text-sky-950 dark:text-white"
              @click="toggleMaximize"
            >
              <img :src="arrowMaximize" class="h-5 w-5" />
            </button>
          </div>
          <div
            v-if="showJroidRef"
            :id="`${IDEVIEWTABS.JDROID}`"
            role="tabpanel"
            :aria-labelledby="`${IDEVIEWTABS.JDROID}-item`"
            class="active"
          >
            <Jdroid
              class="active flex h-screen items-stretch"
              :currentPosition="TAB_BUTTON_POSITION.MAXIMIZE"
              role="tabpanel"
              :id="`${IDEVIEWTABS.JDROID}`"
              :aria-labelledby="`${IDEVIEWTABS.JDROID}-item`"
              :tabPosition="TAB_BUTTON_POSITION.MAXIMIZE"
            />
          </div>

          <div
            v-if="isShowLibs && showExtLibRef"
            :id="`${IDEVIEWTABS.LIBRARIES}`"
            role="tabpanel"
            :aria-labelledby="`${IDEVIEWTABS.LIBRARIES}-item`"
          >
            <div class="h-screen w-screen">
              <LibraryComp :tabPosition="TAB_BUTTON_POSITION.MAXIMIZE" />
            </div>
          </div>
          <div
            v-if="showUploadResRef"
            :id="`${IDEVIEWTABS.INPUTFILES}`"
            role="tabpanel"
            :aria-labelledby="`${IDEVIEWTABS.INPUTFILES}-item`"
          >
            <div class="mt-0 flex h-screen w-screen items-start">
              <InputFileComp :tabPosition="TAB_BUTTON_POSITION.MAXIMIZE" />
            </div>
          </div>
          <div
            v-if="showIORef"
            class="h-full w-full overflow-auto"
            :class="{
              'h-screen w-screen ': isTabMaximized
            }"
            :id="`${IDEVIEWTABS.IO}`"
          >
            <div
              class="flex h-full min-w-[455px] flex-1 flex-col"
              role="tabpanel"
              :aria-labelledby="`${IDEVIEWTABS.INPUT}-item`"
            >
              <TabCompTitleBar
                titleName="Input/Output"
                :icon="inputOutputIcon"
                :tabPosition="TAB_BUTTON_POSITION.MAXIMIZE"
                :tabName="TAB_ICONS_NAMES.IO"
              />
              <div class="surface-secondary-base h-full w-full px-3">
                <InputComp />
                <div
                  class="flex w-full items-end"
                  role="tabpanel"
                  :aria-labelledby="`${IDEVIEWTABS.OUTPUT}-item`"
                >
                  <div
                    :class="[
                      'flex  w-full gap-8  sm:gap-5 lg:flex-col',
                      {
                        'flex-col lg:flex-row': !isFullScreen
                      },
                      {
                        'flex-col': isFullScreen
                      }
                    ]"
                  >
                    <TabsComponent
                      class="flex flex-col"
                      :tabs="[{ label: OutputHeader }, { label: GeneratedFileHeader }]"
                    >
                      <template #slot1>
                        <OutputComp
                          v-if="currentMobileTab === null"
                          :tabPosition="TAB_BUTTON_POSITION.MAXIMIZE"
                        />
                      </template>
                      <template #slot2>
                        <GenaratedFileComp />
                      </template>
                    </TabsComponent>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="showBrowserRef"
            :id="`${IDEVIEWTABS.BROWSER}`"
            class="h-full"
            role="tabpanel"
            :aria-labelledby="`${IDEVIEWTABS.BROWSER}-item`"
          >
            <div class="h-screen w-screen">
              <BrowserComp :tabPosition="TAB_BUTTON_POSITION.MAXIMIZE" />
            </div>
          </div>
          <div
            v-if="showApiTabRef"
            :id="`${IDEVIEWTABS.API_TAB}`"
            class="h-full"
            role="tabpanel"
            :aria-labelledby="`${IDEVIEWTABS.API_TAB}-item`"
          >
            <div class="h-screen w-screen">
              <ApiTabComp :tabPosition="TAB_BUTTON_POSITION.MAXIMIZE" />
            </div>
          </div>

          <div
            v-if="showDbTerminalRef"
            :id="`${IDEVIEWTABS.DB_TERMINAL}`"
            class="h-full"
            role="tabpanel"
            :aria-labelledby="`${IDEVIEWTABS.DB_TERMINAL}-item`"
          >
            <div class="h-screen w-screen">
              <DbTerminal :tabPosition="TAB_BUTTON_POSITION.MAXIMIZE" />
            </div>
          </div>
          <div
            v-if="showTerminalRef"
            :id="`${IDEVIEWTABS.TERMINAL}`"
            class="h-full"
            role="tabpanel"
            :aria-labelledby="`${IDEVIEWTABS.TERMINAL}-item`"
          >
            <div class="h-screen w-screen">
              <TerminalComp :tabPosition="TAB_BUTTON_POSITION.MAXIMIZE" />
            </div>
          </div>
          <div
            v-if="showConsoleRef"
            :id="`${IDEVIEWTABS.CONSOLE}`"
            class="h-full"
            role="tabpanel"
            :aria-labelledby="`${IDEVIEWTABS.CONSOLE}-item`"
          >
            <div class="h-screen w-screen">
              <ConsoleComp :tabPosition="TAB_BUTTON_POSITION.MAXIMIZE" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
